import React from 'react'
import { buildActions } from './actionhelpers'
import { sh, buildState } from './statehelpers'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { SetValue, ClearValue, AppendArrayValue, UpdateArrayValue, ClearArrayValue } from './Actions/AppStateActions'
import { ClearSettings, ClearAccount } from './Actions/CurrentSessionActions'
import { CloseDrawer } from './Actions/DrawerActions'
import { sendToURL } from './Actions/ReduxRouterActions'
import { ShowErrorDispatch, ShowInfoDispatch, ShowWarningDispatch } from './Actions/ShowNotificationActions'
import { store } from './store'
import axios from 'axios'
import jsonpipe from 'jsonpipe'
import { Storage } from 'common-tools/StorageHelper'
import _ from 'common-tools/LodashHelper'

export { compose } from 'redux'
export { ah } from './actionhelpers'
export { sh } from './statehelpers'
export { buildPropTypes, pt } from './proptypehelpers'

export const WithPropsWrapper = injectedProps => WrappedComponent => {
  const HasProps = props => <WrappedComponent {...injectedProps} {...props} />
  return HasProps
}

export const ReduxWrapper = (states = [], actions = []) => {
  return connect(
    buildState(states),
    buildActions(actions)
  )
}

const uuidv4 = require('uuid/v4')
const ShowError = ShowErrorDispatch()
const ShowInfo = ShowInfoDispatch()
const ShowWarning = ShowWarningDispatch()

// ─── Error Message Sanitizer ────────────────────────────────────────────────
// Maps raw .NET / SQL exception text to friendly user-facing copy.
// Raw messages are preserved in console.warn for debugging.
const ERROR_RULES = [
  // .NET runtime exceptions (must come first — most technical)
  { pattern: /object reference not set to an instance of an object/i,     message: 'Something went wrong on our end. Please try again.' },
  { pattern: /sequence contains no elements/i,                             message: 'No matching record was found.' },
  { pattern: /sequence contains more than one (matching )?element/i,       message: 'A data conflict was detected. Please contact support.' },
  { pattern: /invalid cast exception|input string was not in a correct format/i, message: 'An unexpected data format was received. Please try again.' },
  { pattern: /index (was )?out of range/i,                                 message: 'Something went wrong processing that request. Please try again.' },
  { pattern: /stack\s*(trace|overflow)/i,                                  message: 'Something went wrong on our end. Please try again or contact support.' },
  { pattern: /at System\.|at CradleSpace\.|\+--/i,                        message: 'Something went wrong on our end. Please try again or contact support.' },
  { pattern: /\b\w*exception\b/i,                                         message: 'Something went wrong on our end. Please try again or contact support.' },
  { pattern: /\b(null\s*reference|null\s*pointer|unhandled|division by zero)\b/i, message: 'Something went wrong on our end. Please try again.' },

  // JavaScript / Client-side runtime errors (specifically matched to avoid matching backend strings like "Room is null")
  { pattern: /cannot read properties of null/i,                            message: 'An application error occurred. Please try refreshing the page.' },
  { pattern: /cannot read properties of undefined/i,                       message: 'An application error occurred. Please try refreshing the page.' },
  { pattern: /cannot read property .* of/i,                                 message: 'An application error occurred. Please try refreshing the page.' },
  { pattern: /can't access property .* of/i,                               message: 'An application error occurred. Please try refreshing the page.' },
  { pattern: /is not a function/i,                                         message: 'An application error occurred. Please try refreshing the page.' },
  { pattern: /is not a constructor/i,                                      message: 'An application error occurred. Please try refreshing the page.' },
  { pattern: /assignment to constant variable/i,                           message: 'An application error occurred. Please try refreshing the page.' },
  { pattern: /^(unhandled )?typeerror:/i,                                  message: 'An application error occurred. Please try refreshing the page.' },
  { pattern: /^(unhandled )?referenceerror: .* is not defined$/i,          message: 'An application error occurred. Please try refreshing the page.' },

  // SQL / database errors
  { pattern: /duplicate key|unique (key )?constraint|unique index/i,       message: 'This record already exists.' },
  { pattern: /cannot insert the value null|null value in column/i,         message: 'A required field is missing. Please complete all required fields.' },
  { pattern: /deadlock|transaction.*aborted/i,                             message: 'A temporary conflict occurred. Please try again.' },
  { pattern: /string or binary data would be truncated/i,                  message: 'One of the values entered is too long.' },
  { pattern: /foreign key constraint/i,                                    message: 'This record is in use and cannot be changed.' },
  { pattern: /the (multi-part )?identifier .* could not be bound/i,        message: 'Something went wrong on our end. Please try again or contact support.' },

  // Network / connectivity
  { pattern: /timed? out|timeout/i,                                        message: 'The request took too long. Please try again.' },
  { pattern: /unable to connect|connection (refused|failed|reset)|network/i, message: 'Unable to reach the server. Please check your connection.' },

  // Auth / permissions
  { pattern: /unauthorized|401/i,                                          message: 'Your session has expired. Please sign in again.' },
  { pattern: /forbidden|403|you are not allowed to call this method/i,     message: "You don't have permission to perform this action." },

  // Not found
  { pattern: /not found|404|no record found/i,                             message: 'The requested item could not be found.' },

  // Quota / limits
  { pattern: /exceeded|quota|limit reached/i,                              message: 'A limit has been reached. Please contact your administrator.' },
]

// ─── HTTP Status Code Messages ───────────────────────────────────────────────
// Covers all standard 4xx / 5xx codes with user-friendly copy.
const HTTP_STATUS_MESSAGES = {
  // 4xx — Client errors
  400: 'The request was invalid. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  402: 'Payment is required to access this resource.',
  403: "You don't have permission to perform this action.",
  404: 'The requested item could not be found.',
  405: 'This action is not allowed.',
  406: 'The server could not produce a response in the requested format.',
  407: 'Proxy authentication is required.',
  408: 'The request timed out. Please try again.',
  409: 'A conflict occurred. The record may have been modified by someone else.',
  410: 'This resource is no longer available.',
  411: 'A required field is missing from the request.',
  412: 'A precondition for this request was not met.',
  413: 'The data you submitted is too large. Please reduce it and try again.',
  414: 'The request URL is too long.',
  415: 'The file type you submitted is not supported.',
  416: 'The requested data range is not available.',
  417: 'The request could not be completed as expected.',
  418: "I'm a teapot.", // RFC 2324 — keeping it real
  421: 'The request was directed to a server that cannot respond.',
  422: 'The data submitted could not be processed. Please check your input.',
  423: 'This resource is locked.',
  424: 'This request depends on another request that failed.',
  425: 'The server is not ready to process this request yet. Please try again.',
  426: 'A protocol upgrade is required to complete this request.',
  428: 'A precondition is required for this request.',
  429: 'Too many requests. Please wait a moment and try again.',
  431: 'The request headers are too large.',
  451: 'This resource is unavailable for legal reasons.',

  // 5xx — Server errors
  500: 'Something went wrong on our end. Please try again or contact support.',
  501: 'This feature is not yet supported by the server.',
  502: 'The server received an invalid response from an upstream service. Please try again.',
  503: 'The service is temporarily unavailable. Please try again shortly.',
  504: 'The server took too long to respond. Please try again.',
  505: 'The server does not support the HTTP version used in the request.',
  506: 'The server has a configuration error. Please contact support.',
  507: 'The server has run out of storage space. Please contact your administrator.',
  508: 'The server detected an infinite loop while processing your request.',
  510: 'Further extensions are required to fulfil this request.',
  511: 'Network authentication is required.',
}

// Returns a user-friendly message for a given HTTP status code.
// Falls back to a generic message if the code isn\'t in the map.
const getStatusMessage = (status) => {
  return HTTP_STATUS_MESSAGES[status] || `An unexpected error occurred (HTTP ${status}). Please try again or contact support.`
}
// ─────────────────────────────────────────────────────────────────────────────

// Heuristic: does this string still look like raw technical output even
// if no rule matched? (long, no spaces ratio, or contains type namespaces)
const looksLikeTechnicalMessage = (msg) => {
  if (!msg || typeof msg !== 'string') return false
  if (msg.length > 120 && (msg.match(/\s/g) || []).length < msg.length * 0.08) return true
  if (/System\.\w+|CradleSpace\.\w+|Microsoft\.\w+/.test(msg)) return true
  return false
}

const humanizeErrorMessage = (rawMessage) => {
  if (!rawMessage || typeof rawMessage !== 'string') return rawMessage
  const trimmed = rawMessage.trim()
  for (const rule of ERROR_RULES) {
    if (rule.pattern.test(trimmed)) {
      console.warn('[API Error - raw]', trimmed)
      return rule.message
    }
  }
  if (looksLikeTechnicalMessage(trimmed)) {
    console.warn('[API Error - raw]', trimmed)
    return 'Something went wrong. Please try again or contact support.'
  }
  return trimmed
}
// ────────────────────────────────────────────────────────────────────────────

// ─── Server-Side Error Logger ─────────────────────────────────────────────────
// Fire-and-forget: posts a client error to the backend ErrorLog table.
// Never throws and never shows an additional toast.
const logErrorToServer = ({ rawMessage = '', friendlyMessage = '', httpStatus = 0, errorCategory = 'BusinessError' } = {}) => {
  try {
    const bodyFormData = new FormData() // eslint-disable-line
    bodyFormData.set('request', JSON.stringify({
      RawMessage: String(rawMessage).slice(0, 2000),   // cap to avoid DB overflow
      FriendlyMessage: String(friendlyMessage).slice(0, 500),
      HttpStatus: httpStatus || 0,
      ErrorCategory: errorCategory,
      PageUrl: (window.location || {}).href || '',
      UserAgent: (navigator || {}).userAgent || ''
    }))
    axios({
      url: '/Security/LogClientError',
      data: bodyFormData,
      method: 'post',
      headers: { 'token': Storage.Get('HiveToken') || '' }
    }).catch(() => { /* silently swallow logging failures */ })
  } catch (e) {
    // never let logging break anything
  }
}
// ────────────────────────────────────────────────────────────────────────────

const processFunctionResultErrorMessages = (messages) => {
  _.each(messages, (m) => {
    if (m.TypeText === 'Info') {
      ShowInfo(humanizeErrorMessage(m.Message))
    } else if (m.TypeText === 'Warning') {
      ShowWarning(humanizeErrorMessage(m.Message))
    } else if (m.TypeText === 'ModelFieldTypeError') {
      console.log('Model Field Type Error', m.Message)
    } else if (m.TypeText !== 'Rule') {
      const friendly = humanizeErrorMessage(m.Message)
      ShowError(friendly)
      logErrorToServer({
        rawMessage: m.Message,
        friendlyMessage: friendly,
        errorCategory: 'BusinessError'
      })
    }
  })
}

const processFunctionResultSuccessMessages = (messages) => {
  _.each(messages, (m) => {
    if (m.TypeText === 'Info') {
      ShowInfo(m.Message)
    } else if (m.TypeText === 'Warning') {
      ShowWarning(m.Message)
    } else if (m.TypeText === 'ModelFieldTypeError') {
      console.log('Model Field Type Error', m.Message)
    } else if (m.TypeText !== 'Rule') {
      ShowError(m.Message)
    }
  })
}

export function QueryWrapper(options) {
  let queryWrapper = (opts) => {
    return (WrappedComponent) => {
      opts = _.merge({ actions: [], withRef: false, cleanOnUnmount: true }, opts)

      if (!opts.key) {
        opts.key = 'NoKeyProvided'
      }

      if (_.indexOf(opts.key, '.') > -1) {
        throw new Error('Query Wrapper option "key" can not contain a period!')
      }

      opts.statekey = `${opts.key}_${uuidv4()}`

      class QueryWrapperInternal extends React.Component {
        ref = null

        getWrappedInstance() {
          return this.ref
        }

        componentWillUnmount() {
          if (opts.cleanOnUnmount) {
            store.dispatch(ClearValue(opts.statekey))
          }
        }

        getQueryProps = _.once(() => {
          let ret = {}

          _.forEach(opts.actions, (a) => {
            if (ret[a.prop]) {
              throw new Error('action prop name already assigned!')
            }

            if (a.mapIsArray === true & _.isString(a.map) === true) {
              ret[`${a.map}UpdateArrayValue`] = (filterFunction, updateFunction, modifyFunction) => {
                store.dispatch(UpdateArrayValue(opts.statekey, a.map, filterFunction, updateFunction, modifyFunction))
              }
              ret[`${a.map}AppendArrayValue`] = (values) => {
                store.dispatch(AppendArrayValue(opts.statekey, a.map, values))
              }
              ret[`${a.map}ClearArrayValue`] = () => {
                store.dispatch(ClearArrayValue(opts.statekey, a.map))
              }
            }

            if (a.supportsProgress === true) {
              ret[`${a.prop}ClearProgress`] = () => {
                store.dispatch(SetValue(opts.statekey, `progress${a.prop}`, undefined))
              }
            }

            let updateProgress = _.debounce((stateKey, prop, value) => {
              store.dispatch(SetValue(stateKey, prop, value))
            }, 250)

            ret[a.prop] = (data, loadMore = false) => {
              const currentStore = store.getState()
              store.dispatch(SetValue(opts.statekey, `loading${a.prop}`, true))

              let queryParams = _.merge({ Skip: 0 }, data)

              if (loadMore === true && (_.isString(a.map) || _.isFunction(a.map))) {
                queryParams.Skip = ((currentStore.AppState[opts.statekey] || {})[`${a.prop}SkipCount`] || 0) + 1
              }

              let lastCallCount = ((currentStore.AppState[opts.statekey] || {})[`${a.prop}CallCount`] || 0)

              let webCall = null

              if (a.supportsProgress === true) {
                webCall = new Promise((resolve, reject) => {
                  jsonpipe.flow(a.url, {
                    onHeaders: function (statusText, headers) {
                      // Do something with the headers and the statusText.
                      console.log('status text', statusText)
                      console.log('headers', headers)
                    },
                    success: function (data) {
                      if (data.IsProgress === true) {
                        updateProgress(opts.statekey, `progress${a.prop}`, data.ReturnValue || {})
                        // store.dispatch(SetValue(opts.statekey, `progress${a.prop}`, data.ReturnValue || {}))
                      } else {
                        if (data.Status !== 'Success') {
                          processFunctionResultErrorMessages(data.Messages)
                          reject(data.Messages)
                        } else {
                          processFunctionResultSuccessMessages(data.Messages)
                          if (_.isString(a.map)) {
                            if (queryParams.Skip > 0) {
                              store.dispatch(AppendArrayValue(opts.statekey, a.map, data.ReturnValue))
                            } else {
                              store.dispatch(SetValue(opts.statekey, a.map, data.ReturnValue))
                            }
                            if (a.mapIsArray === true) {
                              if (data.ReturnValue.length === queryParams.Limit) {
                                store.dispatch(SetValue(opts.statekey, `${a.map}HasMore`, true))
                              } else {
                                store.dispatch(SetValue(opts.statekey, `${a.map}HasMore`, false))
                              }
                            }
                            store.dispatch(SetValue(opts.statekey, `${a.prop}SkipCount`, queryParams.Skip))
                            resolve({ data: data.ReturnValue, extra: data.ExtraData })
                          } else if (_.isFunction(a.map)) {
                            let mapResult = a.map(data.ReturnValue, queryParams.Skip)
                            if (!_.isEmpty(mapResult)) {
                              _.forEach(_.keys(mapResult), (k) => {
                                store.dispatch(SetValue(opts.statekey, k, mapResult[k]))
                              })
                            }
                            store.dispatch(SetValue(opts.statekey, `${a.prop}SkipCount`, queryParams.Skip))
                            resolve({ data: data.ReturnValue, extra: data.ExtraData })
                          } else {
                            resolve({ data: data.ReturnValue, extra: data.ExtraData })
                          }
                          store.dispatch(SetValue(opts.statekey, `${a.prop}CallCount`, lastCallCount + 1))
                        }
                      }
                    },
                    error: function (errorMsg) {
                      if (errorMsg === 'Unauthorized') {
                        store.dispatch(ClearSettings())
                        store.dispatch(CloseDrawer('all'))
                        store.dispatch(ClearAccount())
                        sendToURL(store.dispatch)('/Login')
                        ShowError('Your session has expired. Please sign in again.')
                        reject([{ Type: 'Authentication', Message: 'Your session has expired. Please sign in again.' }]) //eslint-disable-line
                      } else {
                        ShowError(errorMsg || 'Unexpected Error :(')
                        reject([{ Type: 'Unexpected', Message: errorMsg }]) //eslint-disable-line
                      }
                    },
                    complete: function (statusText) {
                      store.dispatch(SetValue(opts.statekey, `loading${a.prop}`, false))
                    },
                    timeout: 45000,
                    method: 'POST',
                    headers: {
                      'X-Requested-With': 'XMLHttpRequest',
                      'token': Storage.Get(`HiveToken`) || '',
                      'Content-Type': 'application/json;charset=UTF-8'
                    },
                    data: JSON.stringify(queryParams),
                    disableContentType: true, // By default jsonpipe will set `Content-Type` to "application/x-www-form-urlencoded", you can set `disableContentType` to `true` to skip this behavior. Must set `true` if your `data` is not a string.
                    withCredentials: false
                  })
                })
              } else {
                store.dispatch(SetValue('GlobalLoading', `${opts.statekey}loading${a.prop}`, true))
                const bodyFormData = new FormData() // eslint-disable-line
                bodyFormData.set('request', JSON.stringify(queryParams))
                webCall = new Promise((resolve, reject) => {
                  axios({
                    url: a.url,
                    data: bodyFormData,
                    crossDomain: true,
                    method: 'post',
                    headers: {
                      'token': Storage.Get(`HiveToken`) || ''
                    }
                  }).then(resp => {
                    if (resp.data.Status !== 'Success') {
                      processFunctionResultErrorMessages(resp.data.Messages)
                      store.dispatch(SetValue(opts.statekey, `loading${a.prop}`, false))
                      store.dispatch(ClearValue('GlobalLoading', `${opts.statekey}loading${a.prop}`))
                      reject(resp.data.Messages)
                    } else {
                      processFunctionResultSuccessMessages(resp.data.Messages)
                      if (_.isString(a.map)) {
                        if (_.has(resp.data.ReturnValue, 'Items')) {
                          if (queryParams.Skip > 0) {
                            store.dispatch(AppendArrayValue(opts.statekey, a.map, resp.data.ReturnValue.Items))
                          } else {
                            store.dispatch(SetValue(opts.statekey, a.map, resp.data.ReturnValue.Items))
                          }
                        } else {
                          if (queryParams.Skip > 0) {
                            store.dispatch(AppendArrayValue(opts.statekey, a.map, resp.data.ReturnValue))
                          } else {
                            try {
                              store.dispatch(SetValue(opts.statekey, a.map, resp.data.ReturnValue))
                            } catch (error) {
                              console.error(error)
                            }
                          }
                        }
                        if (a.mapIsArray === true) {
                          if (_.has(resp.data.ReturnValue, 'Items')) {
                            if (resp.data.ReturnValue.Items.length === queryParams.Limit) {
                              store.dispatch(SetValue(opts.statekey, `${a.map}HasMore`, true))
                            } else {
                              store.dispatch(SetValue(opts.statekey, `${a.map}HasMore`, false))
                            }
                          } else {
                            if (resp.data.ReturnValue && resp.data.ReturnValue.length === queryParams.Limit) {
                              store.dispatch(SetValue(opts.statekey, `${a.map}HasMore`, true))
                            } else {
                              store.dispatch(SetValue(opts.statekey, `${a.map}HasMore`, false))
                            }
                          }
                        }

                        store.dispatch(SetValue(opts.statekey, `loading${a.prop}`, false))
                        store.dispatch(ClearValue('GlobalLoading', `${opts.statekey}loading${a.prop}`))
                        store.dispatch(SetValue(opts.statekey, `${a.prop}SkipCount`, queryParams.Skip))
                        resolve({ data: resp.data.ReturnValue, extra: resp.data.ExtraData })
                      } else if (_.isFunction(a.map)) {
                        let mapResult = a.map(resp.data.ReturnValue, queryParams.Skip)
                        if (!_.isEmpty(mapResult)) {
                          _.forEach(_.keys(mapResult), (k) => {
                            store.dispatch(SetValue(opts.statekey, k, mapResult[k]))
                          })
                        }
                        store.dispatch(SetValue(opts.statekey, `loading${a.prop}`, false))
                        store.dispatch(ClearValue('GlobalLoading', `${opts.statekey}loading${a.prop}`))
                        store.dispatch(SetValue(opts.statekey, `${a.prop}SkipCount`, queryParams.Skip))
                        resolve({ data: resp.data.ReturnValue, extra: resp.data.ExtraData })
                      } else {
                        store.dispatch(SetValue(opts.statekey, `loading${a.prop}`, false))
                        store.dispatch(ClearValue('GlobalLoading', `${opts.statekey}loading${a.prop}`))
                        resolve({ data: resp.data.ReturnValue, extra: resp.data.ExtraData })
                      }
                      store.dispatch(SetValue(opts.statekey, `${a.prop}CallCount`, lastCallCount + 1))
                    }
                  }).catch(err => {
                    console.log('ajax error', err)
                    const status = err.response ? err.response.status : 0
                    if (status === 401) {
                      store.dispatch(ClearSettings())
                      store.dispatch(CloseDrawer('all'))
                      store.dispatch(ClearAccount())
                      sendToURL(store.dispatch)('/Login')
                      ShowError(getStatusMessage(401))
                      reject([{ Type: 'Authentication', Message: getStatusMessage(401) }]) //eslint-disable-line
                    } else if (status > 0) {
                      // HTTP error with a known status code
                      const friendly = getStatusMessage(status)
                      ShowError(friendly)
                      logErrorToServer({
                        rawMessage: (err.response && err.response.data) ? JSON.stringify(err.response.data).slice(0, 2000) : err.message,
                        friendlyMessage: friendly,
                        httpStatus: status,
                        errorCategory: 'HttpError'
                      })
                      reject(err)
                    } else {
                      // No HTTP response — JS / network error
                      const rawMsg = (err || {}).message || ''
                      const friendly = humanizeErrorMessage(rawMsg) || 'Something went wrong. Please try again.'
                      ShowError(friendly)
                      logErrorToServer({
                        rawMessage: rawMsg,
                        friendlyMessage: friendly,
                        httpStatus: 0,
                        errorCategory: 'NetworkError'
                      })
                      reject(err)
                    }
                    store.dispatch(SetValue(opts.statekey, `loading${a.prop}`, false))
                    store.dispatch(ClearValue('GlobalLoading', `${opts.statekey}loading${a.prop}`))
                  })
                })
              }
              return webCall
            }
          })

          return ret
        })

        render() {
          const queryProps = this.getQueryProps()
          if (opts.withRef) {
            return (
              <WrappedComponent ref={(c) => { this.ref = c }} {...this.props} {...queryProps} AppStateKey={opts.key} />
            )
          } else {
            return (
              <WrappedComponent {...this.props} {...queryProps} AppStateKey={opts.statekey} />
            )
          }
        }
      }

      return compose(
        connect(
          buildState([
            sh.AppState([
              { sourceProp: opts.statekey, destProp: 'data', defaultValue: {} }
            ])
          ])
        )
      )(QueryWrapperInternal)
    }
  }

  if (_.isFunction(options)) {
    return queryWrapper({})(options)
  }

  return queryWrapper(options)
}

// Re-export buildState and add getHelpers stub for legacy views
export { buildState }
export const getHelpers = () => ({})
